import React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { Link } from "react-router-dom";
import logo from "../../assets/images/logo.jpg";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { removeUser } from "../../store/auth";
import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';

import { AiOutlineMenu } from "react-icons/ai";
import {
  MdFileCopy,
  MdLanguage,
  MdLocalHospital,
  MdPowerSettingsNew,
  MdCardMembership,
  MdVpnKey,
  MdLink,
  MdAddCircleOutline,
} from "react-icons/md";
import { MdSupervisedUserCircle, MdOutlinePayment } from "react-icons/md";

import { confirmation } from "../../services/utills";
import { scrollCSS } from "../../App";

const BRAND_ACTIVE = "#A72C68";
const BRAND_HOVER_BG = "#F9EEF5";
const BRAND_HOVER_TEXT = "#A82175";

const sideData = [
  {
    section: "ACCESS CONTROL",
    items: [
      {
        name: "Roles",
        logo: <MdSupervisedUserCircle style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/roles",
        active: ['roles', "edit-role", 'add-role'],
      },
      {
        name: "Permissions",
        logo: <MdVpnKey style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/permissions",
        active: ['permissions', "edit-permissions", 'add-permissions'],
      },
      {
        name: "Menus",
        logo: <AiOutlineMenu style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/menus",
        active: ['menus', "edit-menus", 'add-menus'],
      },
      {
        name: "GHL WebHook",
        logo: <MdLink style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/webhook",
        active: ['webhook', 'add-ghl', 'edit-ghl'],
      },
    ],
  },
  {
    section: "BILLING & PLANS",
    items: [
      {
        name: "Membership Plans",
        logo: <MdCardMembership style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/all-membership-plans",
        active: ['all-membership-plans', "edit-membership", 'add-membership'],
      },
      {
        name: "Payment Methods",
        logo: <MdOutlinePayment style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/payment-methods",
        active: ['payment-methods', "edit-payment-methods", 'add-payment-methods'],
      },
      {
        name: "Languages",
        logo: <MdLanguage style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/languages",
        active: ['languages', "edit-language", 'add-language'],
      },
    ],
  },
  {
    section: "COMMUNICATION",
    items: [
      {
        name: "Notification Template",
        logo: <MdFileCopy style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/notification-templates/customer-signup/1",
        active: [
          'customer-signup', "customer-created-from-crm", "membership-signup",
          "membership-upgraded", "appointment-booked", "appointment-rescheduled",
          "appointment-canceled", "reminder-for-appointment-before",
          "reminder-for-appointment-day", "check-in", "receipt-generated",
          "ask-for-review", "customer-no-show", "clinic-cancel-membership",
          "credit-card-decline",
        ],
      },
      {
        name: "Referral Points",
        logo: <MdAddCircleOutline style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/refralpoints",
        active: ['refralpoints'],
      },
      {
        name: "Video Manual",
        logo: <NaturePeopleIcon style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/usermanual",
        active: ['usermanual'],
      },
      {
        name: "Clinics",
        logo: <MdLocalHospital style={{ fontSize: "1.4rem" }} />,
        url: "/admin/configuration/clinics",
        active: ['clinics', "edit-clinic", 'add-clinic'],
      },
    ],
  },
];

export const drawerWidth = 260;

export const openedMixin = (theme, customWidth) => ({
  width: customWidth || drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

export const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const CustomDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    zIndex: '11',
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ".css-12i7wg6-MuiPaper-root-MuiDrawer-paper": {
      zIndex: '11',
    },
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const AdminSidebar = ({ sidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { pathname } = location;

  const handleLogout = async () => {
    if (await confirmation("Are you want to logout", "Yes, Logout!")) {
      localStorage.removeItem("token");
      dispatch(removeUser());
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
      }
      navigate("/login");
      window.location.reload();
    }
  };

  const handleButtonClick = (val) => {
    navigate(val);
  };

  const checkActive = (activeArray) => {
    let res = pathname.split('/');
    let lastValue;
    if (pathname.includes('/admin/configuration/notification-templates/')) {
      lastValue = res[res?.length - 2];
    } else if (pathname.includes('edit-')) {
      lastValue = res[res?.length - 2];
    } else {
      lastValue = res[res?.length - 1];
    }
    return !!activeArray.includes(lastValue);
  };

  return (
    <CustomDrawer
      sx={{ display: { md: "block", xs: "none", sm: "none", lg: "block" } }}
      variant="permanent"
      open={sidebarOpen}
    >
      <Box
        sx={{
          borderRight: "1px solid #F0E4EC",
          backgroundColor: "#FFFFFF",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          ...scrollCSS,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            px: "16px",
            pt: "18px",
            pb: "12px",
            minHeight: sidebarOpen ? "72px" : "60px",
            display: "flex",
            alignItems: "center",
          }}
        >
          {sidebarOpen && (
            <Link to="">
              <img src={logo} alt="logo" style={{ height: "auto", width: "100%", maxHeight: "44px", objectFit: "contain" }} />
            </Link>
          )}
        </Box>

        {/* Divider */}
        <Box sx={{ height: "1px", backgroundColor: "#F0E4EC", mx: "16px", mb: "8px" }} />

        {/* Nav Sections */}
        <Box sx={{ flex: 1, px: "10px", pb: "8px", overflowY: "auto", ...scrollCSS }}>
          {sideData.map((group, gIndex) => (
            <Box key={gIndex} sx={{ mb: "6px" }}>
              {sidebarOpen && (
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: "#9CA3AF",
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    px: "10px",
                    pt: gIndex === 0 ? "4px" : "10px",
                    pb: "4px",
                    display: "block",
                  }}
                >
                  {group.section}
                </Typography>
              )}
              <List component="nav" sx={{ p: 0, ".MuiListItemIcon-root": { minWidth: "34px" } }}>
                {group.items.map((item, index) => {
                  const isActive = checkActive(item.active);
                  return (
                    <ListItemButton
                      key={index}
                      onClick={() => handleButtonClick(item.url)}
                      sx={{
                        borderRadius: "8px",
                        mb: "3px",
                        py: "10px",
                        px: "12px",
                        transition: "all 0.15s ease",
                        backgroundColor: isActive ? BRAND_ACTIVE : "transparent",
                        color: isActive ? "#FFFFFF" : "#111111",
                        ".MuiListItemIcon-root": {
                          color: isActive ? "#FFFFFF" : "#111111",
                          minWidth: "38px",
                        },
                        "&:hover": {
                          backgroundColor: isActive ? BRAND_ACTIVE : BRAND_HOVER_BG,
                          color: isActive ? "#FFFFFF" : BRAND_HOVER_TEXT,
                          ".MuiListItemIcon-root": {
                            color: isActive ? "#FFFFFF" : BRAND_HOVER_TEXT,
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: "inherit" }}>
                        {item.logo}
                      </ListItemIcon>
                      {sidebarOpen && (
                        <ListItemText
                          primary={item.name}
                          primaryTypographyProps={{
                            fontSize: "0.92rem",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            color: "inherit",
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        {/* Logout */}
        <Box sx={{ px: "10px", pb: "16px" }}>
          <Box sx={{ height: "1px", backgroundColor: "#F0E4EC", mb: "10px" }} />
          <ListItemButton
            onClick={() => handleLogout()}
            sx={{
              borderRadius: "8px",
              py: "7px",
              px: "10px",
              border: "1px solid #FEE2E2",
              backgroundColor0: "#FEF2F2",
              color: "#EF4444",
              ".MuiListItemIcon-root": {
                color: "inherit",
                minWidth: "34px",
              },
              "&:hover": {
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
              },
            }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <MdPowerSettingsNew style={{ fontSize: "1.4rem" }} />
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontWeight: 700, fontSize: "0.92rem" }}
              />
            )}
          </ListItemButton>
        </Box>
      </Box>
    </CustomDrawer>
  );
};

export default AdminSidebar;
